import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getAgenticPrompts } from 'services/agenticPrompts'
import { AGENTIC_PROMPT_SCOPES } from 'constants/agenticPrompts'
import debounce from 'utils/debounce'

const defaultFilters = {
  term: null,
  categories: null,
  workflows: null,
  scope: AGENTIC_PROMPT_SCOPES.ALL,
  statuses: null,
}

export const AGENTIC_PROMPTS_QUERY_KEY = 'agenticPromptsQuery'

function useAgenticPrompts(initialFilters = {}) {
  const [filters, setFilters] = useState({ ...defaultFilters, ...initialFilters })

  const { statuses, ...apiFilters } = filters

  // isPending (not isLoading) so the grid keeps showing its skeleton for a filter change
  // that has no cached data yet, rather than flashing an empty table.
  const { isPending, data } = useQuery({
    queryKey: [AGENTIC_PROMPTS_QUERY_KEY, apiFilters],
    queryFn: () => getAgenticPrompts(apiFilters),
    refetchOnWindowFocus: false,
    throwOnError: true,
    structuralSharing: false,
  })

  const all = data?.data ?? []
  const prompts = statuses?.length ? all.filter(p => statuses.includes(p.status)) : all

  const debouncedSetTerm = useMemo(
    () => debounce(term => setFilters(prev => ({ ...prev, term })), 300),
    [],
  )

  const handleSearch = useCallback(
    event => {
      const term = event.target.value || null
      debouncedSetTerm(term)
    },
    [debouncedSetTerm],
  )

  function handleCategoriesChange(categories) {
    setFilters(prev => ({
      ...prev,
      categories: categories?.length ? categories : null,
    }))
  }

  function handleWorkflowsChange(workflows) {
    setFilters(prev => ({
      ...prev,
      workflows: workflows?.length ? workflows : null,
    }))
  }

  function handleStatusesChange(newStatuses) {
    setFilters(prev => ({
      ...prev,
      statuses: newStatuses?.length ? newStatuses : null,
    }))
  }

  return {
    prompts,
    filters,
    apiFilters,
    isPending,
    handleSearch,
    handleCategoriesChange,
    handleWorkflowsChange,
    handleStatusesChange,
  }
}

export { useAgenticPrompts }
